import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { Outlet } from 'react-router';
import PropTypes from 'prop-types';
import { Menu, X, LogOut } from 'lucide-react';
import BaseLayout from './BaseLayout';
import { env } from '../config/env';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { authApi } from '../services/authApi';
import ThemeToggle from '../components/ThemeToggle';
import { Button } from '../ui/components';

// Navigation links - public pages visible to all users
const publicPages = ['Home', 'About'];
const publicRoutes = ['/', '/about'];

// Navigation links - private pages visible only to logged-in users
const privatePages = ['Dashboard', 'Settings'];
const privateRoutes = ['/dashboard', '/settings'];

const PublicLayout = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleLogout = async () => {
    try {
      await authApi.endpoints.logout.initiate().unwrap();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      dispatch(logout());
      navigate("/");
    }
  };

  const navPages = isAuthenticated ? privatePages : publicPages;
  const navRoutes = isAuthenticated ? privateRoutes : publicRoutes;

  return (
    <BaseLayout>
      {/* App Bar */}
      <header style={{ backgroundColor: 'var(--ds-bg-primary)', borderBottom: '1px solid var(--ds-border-default)', position: 'relative', zIndex: 100 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 64 }}>
          {/* Logo */}
          <RouterLink to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/loanknow-logo.svg" alt="LoanKnow" style={{ height: 40, width: 'auto' }} />
          </RouterLink>

          {/* Desktop nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, marginLeft: 32 }} className="lk-desktop-only">
            {navPages.map((page, index) => (
              <RouterLink
                key={page}
                to={navRoutes[index]}
                style={{ padding: '8px 16px', fontSize: '0.875rem', fontWeight: 500, color: 'var(--ds-text-secondary)', textDecoration: 'none', borderRadius: 'var(--ds-border-radius-medium)' }}
              >
                {page}
              </RouterLink>
            ))}
          </nav>

          {/* Right side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAuthenticated ? (
              <>
                <span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>
                  {user?.username || 'User'}
                </span>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut size={16} />
                </Button>
              </>
            ) : (
              <RouterLink to="/login" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm">Login</Button>
              </RouterLink>
            )}
            <ThemeToggle />
            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lk-mobile-only"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: 'var(--ds-text-primary)' }}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="lk-mobile-only" style={{ borderTop: '1px solid var(--ds-border-default)', padding: '8px 24px 16px', backgroundColor: 'var(--ds-bg-primary)' }}>
            {navPages.map((page, index) => (
              <RouterLink
                key={page}
                to={navRoutes[index]}
                onClick={() => setMobileMenuOpen(false)}
                style={{ display: 'block', padding: '10px 0', fontSize: '0.875rem', fontWeight: 500, color: 'var(--ds-text-primary)', textDecoration: 'none' }}
              >
                {page}
              </RouterLink>
            ))}
          </div>
        )}
      </header>
      
      {/* Main content area */}
      <main style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%', padding: 16 }}>
        {children || <Outlet />}
      </main>
      
      {/* Footer */}
      <footer style={{ padding: '24px 0', backgroundColor: 'var(--ds-bg-primary)', textAlign: 'center', width: '100%' }}>
        <p style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>
          © {new Date().getFullYear()} {env.appTitle}
        </p>
        <p style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>
          Environment: {env.appEnv}
        </p>
      </footer>
    </BaseLayout>
  );
};

PublicLayout.propTypes = {
  children: PropTypes.node,
};

export default PublicLayout;
